import { useEffect, useLayoutEffect, useMemo, useState } from 'react';

const DOT_RADIUS = 5;
const CHEVRON_W = 2;
const CHEVRON_H = 2;
const CHEVRON_STROKE = 1;
const CHEVRON_OFFSET_X = 1.25;
const CHEVRON_OFFSET_Y = 0;
const PATH_COLOR = 'rgb(209 213 219)';
const DOT_FILL = 'rgb(209 213 219)';
const CHEVRON_COLOR = 'white';
const PATH_WIDTH = 3;
const CORNER_RADIUS = 10;
const DASHED_OPACITY = 0.45;
const PORT_OFFSET = 10;
const NODE_GAP = 15;
const LINE_TO_DOT_GAP = DOT_RADIUS;

function setupThrottledListeners(measureFn) {
    let frame = null;

    const onChange = () => {
        if (frame) cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
            frame = null;
            measureFn();
        });
    };

    return {
        add() {
            window.addEventListener('resize', onChange);
            window.addEventListener('scroll', onChange, { passive: true });
        },
        remove() {
            window.removeEventListener('resize', onChange);
            window.removeEventListener('scroll', onChange);
            if (frame) {
                cancelAnimationFrame(frame);
                frame = null;
            }
        }
    };
}

function getAnchor(rect, side) {
    switch (side) {
        case 'left': return rect.leftMid;
        case 'right': return rect.rightMid;
        case 'top': return rect.topMid;
        case 'bottom': return rect.bottomMid;
        case 'center':
        default: return rect.center;
    }
}

function getAnchored(rect, side, index = 0, total = 1) {
    const t = total > 1 ? (index + 1) / (total + 1) : 0.5;

    switch (side) {
        case 'left': return { x: rect.x, y: rect.y + rect.h * t };
        case 'right': return { x: rect.x + rect.w, y: rect.y + rect.h * t };
        case 'top': return { x: rect.x + rect.w * t, y: rect.y };
        case 'bottom': return { x: rect.x + rect.w * t, y: rect.y + rect.h };
        case 'center':
        default: return { x: rect.center.x, y: rect.center.y };
    }
}

function getOffsetPoint(point, side, offset) {
    switch (side) {
        case 'left': return { x: point.x - offset, y: point.y };
        case 'right': return { x: point.x + offset, y: point.y };
        case 'top': return { x: point.x, y: point.y - offset };
        case 'bottom': return { x: point.x, y: point.y + offset };
        case 'center':
        default: return { x: point.x, y: point.y };
    }
}

function compactPoints(points) {
    if (!Array.isArray(points) || points.length < 2) return points || [];

    const result = [points[0]];

    for (let i = 1; i < points.length; i++) {
        const prev = result[result.length - 1];
        const curr = points[i];

        if (!curr) continue;
        if (prev.x === curr.x && prev.y === curr.y) continue;

        result.push(curr);
    }

    if (result.length < 3) return result;

    const compacted = [result[0]];

    for (let i = 1; i < result.length - 1; i++) {
        const prev = compacted[compacted.length - 1];
        const curr = result[i];
        const next = result[i + 1];

        const sameVertical = prev.x === curr.x && curr.x === next.x;
        const sameHorizontal = prev.y === curr.y && curr.y === next.y;

        if (sameVertical || sameHorizontal) continue;

        compacted.push(curr);
    }

    compacted.push(result[result.length - 1]);

    return compacted;
}

function toRoundedPath(points, radius = CORNER_RADIUS) {
    if (!points || !points.length) return '';
    if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;

    const dir = (a, b) => ({ x: Math.sign(b.x - a.x), y: Math.sign(b.y - a.y) });

    let d = `M ${points[0].x} ${points[0].y}`;

    for (let i = 1; i < points.length; i++) {
        const prev = points[i - 1];
        const curr = points[i];
        const next = points[i + 1];

        if (!next) {
            d += `L ${curr.x} ${curr.y}`;
            break;
        }

        const v1 = dir(prev, curr);
        const v2 = dir(curr, next);

        if (v1.x === v2.x && v1.y === v2.y) {
            d += `L ${curr.x} ${curr.y}`;
            continue;
        }

        const seg1 = Math.hypot(curr.x - prev.x, curr.y - prev.y);
        const seg2 = Math.hypot(next.x - curr.x, next.y - curr.y);
        const cut = Math.min(radius, seg1 / 2, seg2 / 2);

        const cornerIn = { x: curr.x - v1.x * cut, y: curr.y - v1.y * cut };
        const cornerOut = { x: curr.x + v2.x * cut, y: curr.y + v2.y * cut };

        d += ` L ${cornerIn.x} ${cornerIn.y} Q ${curr.x} ${curr.y} ${cornerOut.x} ${cornerOut.y}`;
    }

    return d;
}

export default function PipelineConnectorOverlay({ wrapperRef, nodeRefs, anchorRefs, nodes, nodeById, connections }) {
    const [layout, setLayout] = useState({ width: 0, height: 0, rects: {} });

    const measure = () => {
        const wrapper = wrapperRef.current;
        if (!wrapper) return;

        const wrect = wrapper.getBoundingClientRect();
        const rects = {};

        for (const n of nodes) {
            const outerEl = nodeRefs.current[n.id];
            if (!outerEl || !outerEl.isConnected) continue;

            const anchorCandidate = anchorRefs?.current?.[n.id];
            const hasValidAnchor = anchorCandidate && anchorCandidate.isConnected && typeof anchorCandidate.getBoundingClientRect === 'function';
            const anchorEl = hasValidAnchor ? anchorCandidate : outerEl;

            let r = anchorEl.getBoundingClientRect();
            if ((!r.width && !r.height) && anchorEl !== outerEl) r = outerEl.getBoundingClientRect();

            const left = r.left - wrect.left;
            const top = r.top - wrect.top;

            rects[n.id] = {
                x: left,
                y: top,
                w: r.width,
                h: r.height,
                center: { x: left + r.width / 2, y: top + r.height / 2 },
                leftMid: { x: left, y: top + r.height / 2 },
                rightMid: { x: left + r.width, y: top + r.height / 2 },
                topMid: { x: left + r.width / 2, y: top },
                bottomMid: { x: left + r.width / 2, y: top + r.height },
                bottom: top + r.height,
                right: left + r.width
            };
        }

        setLayout({ width: wrect.width, height: wrect.height, rects });
    };

    useLayoutEffect(() => {
        measure();

        const id = requestAnimationFrame(measure);
        return () => cancelAnimationFrame(id);
    }, [nodes, connections]);

    useEffect(() => {
        const wrapper = wrapperRef.current;
        if (!wrapper) return;

        let frame = null;

        const scheduleMeasure = () => {
            if (frame) cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => {
                frame = null;
                measure();
            });
        };

        const ro = new ResizeObserver(scheduleMeasure);

        ro.observe(wrapper);

        nodes.forEach((node) => {
            const outerEl = nodeRefs.current[node.id];
            const anchorEl = anchorRefs?.current?.[node.id];

            if (outerEl && outerEl.isConnected) ro.observe(outerEl);
            if (anchorEl && anchorEl.isConnected && anchorEl !== outerEl) ro.observe(anchorEl);
        });

        const throttled = setupThrottledListeners(scheduleMeasure);
        throttled.add();
        scheduleMeasure();

        return () => {
            ro.disconnect();
            throttled.remove();

            if (frame) {
                cancelAnimationFrame(frame);
                frame = null;
            }
        };
    }, [nodes]);

    const paths = useMemo(() => {
        if (!layout.width || !layout.height) return [];

        const fromSideMap = {};
        const toSideMap = {};

        connections.forEach((c, idx) => {
            if (!fromSideMap[c.from]) fromSideMap[c.from] = {};
            if (!fromSideMap[c.from][c.fromSide || 'center']) fromSideMap[c.from][c.fromSide || 'center'] = [];
            fromSideMap[c.from][c.fromSide || 'center'].push(idx);

            if (!toSideMap[c.to]) toSideMap[c.to] = {};
            if (!toSideMap[c.to][c.toSide || 'center']) toSideMap[c.to][c.toSide || 'center'] = [];
            toSideMap[c.to][c.toSide || 'center'].push(idx);
        });

        let marginUnder = 24;

        if (wrapperRef.current) {
            const style = getComputedStyle(wrapperRef.current);
            const rowGap = parseInt(style.rowGap || style.gap || '24', 10);
            if (!Number.isNaN(rowGap)) marginUnder = Math.max(8, Math.round(rowGap / 2));
        }

        const result = [];

        for (let idx = 0; idx < connections.length; idx++) {
            const c = connections[idx];
            const Ameta = nodeById[c.from];
            const Bmeta = nodeById[c.to];
            const A = layout.rects[c.from];
            const B = layout.rects[c.to];

            if (!A || !B || !Ameta || !Bmeta) continue;

            const fromSide = c.fromSide || 'center';
            const toSide = c.toSide || 'center';

            const fromList = fromSideMap[c.from]?.[fromSide] || [idx];
            const toList = toSideMap[c.to]?.[toSide] || [idx];
            const fromIndex = Math.max(0, fromList.indexOf(idx));
            const toIndex = Math.max(0, toList.indexOf(idx));

            const startAnchor = fromList.length > 1 ? getAnchored(A, fromSide, fromIndex, fromList.length) : getAnchor(A, fromSide);
            const endAnchor = toList.length > 1 ? getAnchored(B, toSide, toIndex, toList.length) : getAnchor(B, toSide);

            const startVisible = getOffsetPoint(startAnchor, fromSide, NODE_GAP);
            const endVisible = getOffsetPoint(endAnchor, toSide, NODE_GAP);
            const startOuter = getOffsetPoint(startVisible, fromSide, PORT_OFFSET);
            const endOuter = getOffsetPoint(endVisible, toSide, PORT_OFFSET);

            const route = c.route || 'auto';
            const isSameRow = Ameta.row === Bmeta.row;
            const isBottomToBottom = fromSide === 'bottom' && toSide === 'bottom';

            let pts;

            if (route === 'under' || (route === 'auto' && isBottomToBottom && isSameRow)) {
                const rowBottom = Math.max(A.bottom, B.bottom);
                const yUnder = rowBottom + marginUnder;

                pts = [
                    { x: startVisible.x, y: startVisible.y },
                    { x: startOuter.x, y: startOuter.y },
                    { x: startOuter.x, y: yUnder },
                    { x: endOuter.x, y: yUnder },
                    { x: endOuter.x, y: endOuter.y },
                    { x: endVisible.x, y: endVisible.y }
                ];
            } else if (route === 'hfirst') {
                pts = [
                    { x: startVisible.x, y: startVisible.y },
                    { x: startOuter.x, y: startOuter.y },
                    { x: endOuter.x, y: startOuter.y },
                    { x: endOuter.x, y: endOuter.y },
                    { x: endVisible.x, y: endVisible.y }
                ];
            } else if (route === 'vfirst') {
                pts = [
                    { x: startVisible.x, y: startVisible.y },
                    { x: startOuter.x, y: startOuter.y },
                    { x: startOuter.x, y: endOuter.y },
                    { x: endOuter.x, y: endOuter.y },
                    { x: endVisible.x, y: endVisible.y }
                ];
            } else {
                const horizontalFirst = Math.abs(startOuter.x - endOuter.x) > Math.abs(startOuter.y - endOuter.y);

                pts = horizontalFirst
                    ? [
                        { x: startVisible.x, y: startVisible.y },
                        { x: startOuter.x, y: startOuter.y },
                        { x: endOuter.x, y: startOuter.y },
                        { x: endOuter.x, y: endOuter.y },
                        { x: endVisible.x, y: endVisible.y }
                    ]
                    : [
                        { x: startVisible.x, y: startVisible.y },
                        { x: startOuter.x, y: startOuter.y },
                        { x: startOuter.x, y: endOuter.y },
                        { x: endOuter.x, y: endOuter.y },
                        { x: endVisible.x, y: endVisible.y }
                    ];
            }

            pts = compactPoints(pts);

            const startPath = getOffsetPoint(startVisible, fromSide, LINE_TO_DOT_GAP);
            pts[0] = startPath;

            const prevToEnd = pts[pts.length - 2] || startPath;
            const dx = endVisible.x - prevToEnd.x;
            const dy = endVisible.y - prevToEnd.y;

            const endPath = Math.abs(dx) > Math.abs(dy)
                ? { x: endVisible.x - Math.sign(dx || 1) * LINE_TO_DOT_GAP, y: endVisible.y }
                : { x: endVisible.x, y: endVisible.y - Math.sign(dy || 1) * LINE_TO_DOT_GAP };

            pts[pts.length - 1] = endPath;

            const prev = pts[pts.length - 2] || startPath;
            const end = endPath;
            const segAngleRad = Math.atan2(end.y - prev.y, end.x - prev.x);

            const inwardDegMap = { right: 180, left: 0, top: 90, bottom: -90 };
            const forcedAngleDeg = inwardDegMap[toSide];
            const angleDeg = typeof forcedAngleDeg === 'number' ? forcedAngleDeg : segAngleRad * (180 / Math.PI);
            const angleRad = angleDeg * (Math.PI / 180);

            const offsetX = CHEVRON_OFFSET_X * Math.cos(angleRad) - CHEVRON_OFFSET_Y * Math.sin(angleRad);
            const offsetY = CHEVRON_OFFSET_X * Math.sin(angleRad) + CHEVRON_OFFSET_Y * Math.cos(angleRad);

            result.push({
                d: toRoundedPath(pts),
                start: startVisible,
                end: endVisible,
                angle: angleDeg,
                offsetX,
                offsetY,
                dashed: !!c.dashed
            });
        }

        return result;
    }, [connections, layout, nodeById, wrapperRef]);

    if (!layout.width || !layout.height) return null;

    return (
        <svg className='pointer-events-none absolute inset-0' width={layout.width} height={layout.height} viewBox={`0 0 ${layout.width} ${layout.height}`}>
            {paths.map((p, i) => (
                <g key={i}>
                    <path
                        id={`path-${i}`}
                        d={p.d}
                        fill='none'
                        stroke={PATH_COLOR}
                        strokeWidth={PATH_WIDTH}
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeDasharray={p.dashed ? '8 6' : undefined}
                        opacity={p.dashed ? DASHED_OPACITY : 1}
                    />

                    <circle cx={p.start.x} cy={p.start.y} r={DOT_RADIUS} fill={DOT_FILL} />
                    <circle cx={p.end.x} cy={p.end.y} r={DOT_FILL ? DOT_RADIUS : 0} fill={DOT_FILL} />

                    <g transform={`translate(${p.end.x + p.offsetX}, ${p.end.y + p.offsetY}) rotate(${p.angle})`} opacity={p.dashed ? DASHED_OPACITY : 1}>
                        <path
                            d={`M ${-CHEVRON_W} ${-CHEVRON_H} L 0 0 L ${-CHEVRON_W} ${CHEVRON_H}`}
                            stroke={CHEVRON_COLOR}
                            strokeWidth={CHEVRON_STROKE}
                            fill='none'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                        />
                    </g>

                    {connections[i]?.label && (
                        <text fontSize='10' fill='#6b7280' opacity={p.dashed ? DASHED_OPACITY : 1}>
                            <textPath href={`#path-${i}`} startOffset='50%' textAnchor='middle'>
                                {connections[i].label}
                            </textPath>
                        </text>
                    )}
                </g>
            ))}
        </svg>
    );
}